import { BehaviorSubject, Subject } from 'rxjs';
import { IDetailInfoDataHttp } from 'common/model/common.model';
import { DataTypes, InputControllerTypes, InputDataSourceTypes } from 'common/enums';
import {
  inputControllerTypesToValue,
  optionWithCommentRequired,
  valueToControllerType
} from 'common/helper';
import { DetailGroupInfo, DetailInfo, IDetailValue } from 'common/model';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl
} from '@angular/forms';
import { AttendanceGridRow } from 'attendance/model/attendance.model';
import type { IRepeatableGridColumn } from 'shared/detail-info-type/detail-info-repeatable-table/detail-info-repeatable-table.component';
import moment from 'moment';

export const DetailInfoFormStore = {
  submitted$: new BehaviorSubject<boolean>(false),
  touched$: new Subject<void>(),
  reset$: new Subject<void>(),
  successMsgKey: 'formSubmitSuccessMsg',
  token: ''
};

// ---- RepeatableTable (field-scale grid) helpers -------------------------------------------
// A RepeatableTable FIELD defines its COLUMNS as JSON descriptors stored in the field's option
// rows (inputContentDatas: key = column guid, value = JSON {columnGuid,label,controllerType,
// options[{key,value}],mandatory,sortOrder}). At fill time the field's control is a FormArray of
// row FormGroups (keyed by column guid) rendered by app-detail-info-repeatable-table.

const tableColumnsCache = new WeakMap<DetailInfo, IRepeatableGridColumn[]>();

// Parse a RepeatableTable field's column descriptors into single-field grid columns (synthetic
// DetailInfo per column). Cached per DetailInfo instance - the definition never changes at fill
// time. Non-JSON option rows are skipped (tolerates legacy/foreign rows).
export const parseRepeatableTableColumns = (
  tableField: DetailInfo
): IRepeatableGridColumn[] => {
  const cached = tableColumnsCache.get(tableField);
  if (cached) {
    return cached;
  }

  const defs: any[] = [];
  (tableField.inputContentDatas || []).forEach(({ value }) => {
    try {
      const def = JSON.parse(value);
      if (def && def.columnGuid && def.controllerType) {
        defs.push(def);
      }
    } catch {
      // not a column descriptor - ignore
    }
  });
  defs.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const columns: IRepeatableGridColumn[] = defs.map((def) => {
    const optionDict: { [key: string]: string } = {};
    const optionOrder: string[] = [];
    (def.options || []).forEach((o: any) => {
      if (o && o.key !== undefined && o.key !== null) {
        optionDict[o.key] = o.value;
        optionOrder.push(String(o.key));
      }
    });
    const field = DetailInfo.fromJson(
      {
        evideInputContentGuid: def.columnGuid,
        title: def.label || '',
        description: '',
        sortOrder: def.sortOrder ?? 0,
        isRequired: false,
        isMandatory: !!def.mandatory,
        isHidden: false,
        hasDefaultValue: false,
        defaultValue: null,
        defaultValues: null,
        isUnique: false,
        maxValue: null,
        minValue: null,
        regEx: null,
        detailVisible: true,
        dataType: undefined,
        dataSourceType: undefined,
        formType: tableField.formType,
        gridEdit: true,
        gridHeaderEdit: false,
        controllerType: def.controllerType,
        inputContentDatas: optionDict,
        inputContentDataOrder: optionOrder,
        validationInfos: []
      } as any,
      tableField.ruleParemeters || {}
    );
    return { title: def.label || '', fields: [field] };
  });

  tableColumnsCache.set(tableField, columns);
  return columns;
};

// Build the FormArray for a RepeatableTable field: one FormGroup per saved row (controls keyed
// by column guid, seeded from the row's cells). detailInfo.key holds the saved tableRows.
export const generateFormArrayForTableField = (
  detailInfo: DetailInfo
): FormArray => {
  const columns = parseRepeatableTableColumns(detailInfo);
  const arr = new FormArray<FormGroup>([]);
  const savedRows: any[][] = Array.isArray(detailInfo.key) ? (detailInfo.key as unknown as any[][]) : [];

  (savedRows || []).forEach((rowCells) => {
    const rowGroup = new FormGroup({});
    columns.forEach((col) => {
      col.fields.forEach((field) => {
        const cloned = field.clone();
        assignDetailInfoValue(cloned, (rowCells || []) as IDetailValue[]);
        generateFormGroupForDetailInfo(rowGroup, cloned);
      });
    });
    arr.push(rowGroup);
  });

  return arr;
};

// Legacy/imported boolean text in option-backed fields: spreadsheets (and Excel boolean cells)
// feed "True"/"FALSE"/"1" into Yes/No-style dropdowns, and the raw text was stored when it
// matched no option key — so grids showed "True" and the edit control showed blank. Resolve
// such a stored value to the option whose LABEL is a synonym of the same polarity, but only
// when exactly ONE option qualifies; anything else keeps the raw-value fallback. Read-side
// twin of the universal importer's ResolveOptionKey boolean fallback — display heals
// immediately and the proper option key is written on the next save.
const TRUTHY_SYNONYMS = ['true', 'yes', 'y', '1'];
const FALSY_SYNONYMS = ['false', 'no', 'n', '0'];

export const resolveBooleanSynonymOption = <T extends { key: any; value: any }>(
  options: ReadonlyArray<T> | null | undefined,
  storedValue: any
): T | null => {
  if (storedValue === null || storedValue === undefined || !Array.isArray(options)) {
    return null;
  }
  const lower = String(storedValue).trim().toLowerCase();
  const synonyms = TRUTHY_SYNONYMS.includes(lower)
    ? TRUTHY_SYNONYMS
    : FALSY_SYNONYMS.includes(lower)
      ? FALSY_SYNONYMS
      : null;
  if (!synonyms) {
    return null;
  }
  const matches = options.filter((o) =>
    synonyms.includes(String(o?.value ?? '').trim().toLowerCase())
  );
  return matches.length === 1 ? matches[0] : null;
};

// Used while Getting records
export const assignDetailInfoValue = (
  detailInfo: DetailInfo,
  detailValue: IDetailValue[]
) => {
  const valueObject = detailValue.find(
    (detail) =>
      detail.evideInputContentGuid === detailInfo.evideInputContentGuid
  );

  if (valueObject) {
    if (detailInfo.controllerType === InputControllerTypes.RepeatableTable) {
      // Rows of cells; hydrated into a FormArray by generateFormGroupForDetailInfo.
      detailInfo.key = (valueObject.tableRows as any) || [];
      detailInfo.value = '';
    } else if (detailInfo.controllerType === InputControllerTypes.DropdownMulti) {
      if (valueObject.dataStringValues?.length) {
        // Boolean-text healing: an id matching no option key but reading as true/false
        // resolves to the Yes/No-style option (see resolveBooleanSynonymOption) — label
        // shows AND the option's real key goes into the control, so a save writes it.
        const resolved = valueObject.dataStringValues.map((idValue) => {
          const inputContentData =
            detailInfo.inputContentDatas.find(({ key }) => key == idValue) ??
            resolveBooleanSynonymOption(detailInfo.inputContentDatas, idValue);
          return inputContentData ?? { key: idValue, value: idValue };
        });
        detailInfo.value = resolved.map((o) => o.value);
        detailInfo.key = resolved.map((o) => String(o.key)); // string[] of IDs
      } else {
        // No IDs – clear selection
        detailInfo.value = [];
        detailInfo.key = [];
      }
    } else if (detailInfo.controllerType === InputControllerTypes.Dropdown) {
      if (valueObject.dataStringValue) {
        const inputContentData =
          detailInfo.inputContentDatas.find(
            ({ key }) => key == valueObject.dataStringValue
          ) ??
          resolveBooleanSynonymOption(
            detailInfo.inputContentDatas,
            valueObject.dataStringValue
          );
        if (inputContentData) {
          detailInfo.value = inputContentData.value;
          // The option's key, not the raw stored value — identical for a normal match,
          // and the healed key for boolean text (so the next save stores it properly).
          detailInfo.key = String(inputContentData.key);
        } else {
          detailInfo.value = valueObject.dataStringValue;
          detailInfo.key = valueObject.dataStringValue;
        }
      } else {
        detailInfo.value = '';
        detailInfo.key = '';
      }
    } else if (
      [InputControllerTypes.Checkbox, InputControllerTypes.Checkboxes].includes(
        detailInfo.controllerType
      )
    ) {
      const convertToExtraValue = (idValue, extravalue?: string) => {
        // dataStringValue as value
        const inputContentData = detailInfo.inputContentDatas.find(
          ({ key }) => key == idValue
        );
        if (inputContentData) {
          return {
            dataStringValue: inputContentData.value,
            dataStringExtraValue: extravalue
          };
        } else {
          return { dataStringValue: idValue, dataStringExtraValue: extravalue };
        }
      };

      const convertToExtraValueId = (idValue, extravalue?: string) => {
        // dataStringValue as key
        return { dataStringValue: idValue, dataStringExtraValue: extravalue };
      };

      const dataStringValuesKeys =
        valueObject.dataStringValues?.map((idValue) =>
          convertToExtraValueId(idValue)
        ) ?? [];
      const extraValuesKeys =
        valueObject.dataStringExtraValues?.map(
          ({ dataStringValue, dataStringExtraValue }) =>
            convertToExtraValueId(dataStringValue, dataStringExtraValue)
        ) ?? [];
      detailInfo.key = [...(dataStringValuesKeys ?? []), ...extraValuesKeys]; // IDataStringExtraValue

      const stringValues =
        valueObject.dataStringValues?.map((idValue) =>
          convertToExtraValue(idValue)
        ) ?? [];
      const extraValues =
        valueObject.dataStringExtraValues?.map(
          ({ dataStringValue, dataStringExtraValue }) =>
            convertToExtraValue(dataStringValue, dataStringExtraValue)
        ) ?? [];
      detailInfo.value = [...stringValues, ...extraValues]; // IDataStringExtraValue
    } else if (
      [
        InputControllerTypes.DropdownWithExtraText,
        InputControllerTypes.RadioButtons
      ].includes(detailInfo.controllerType)
    ) {
      const inputContentData = detailInfo.inputContentDatas.find(
        ({ key }) => key == valueObject.dataStringValue
      );
      if (inputContentData) {
        detailInfo.value = {
          dataStringValue: inputContentData.value,
          dataStringExtraValue: valueObject.dataStringExtraValue
        }; // IDataStringExtraValue
        detailInfo.key = {
          dataStringValue: inputContentData.key,
          dataStringExtraValue: valueObject.dataStringExtraValue
        }; // IDataStringExtraValue
      } else if (valueObject.dataStringValue) {
        // Option key no longer present in the current options (e.g. a template option was removed
        // after the value was saved). Preserve the raw stored value+extra so the selection isn't
        // silently dropped on reload (mirrors the single Dropdown branch's fallback above).
        detailInfo.value = {
          dataStringValue: valueObject.dataStringValue,
          dataStringExtraValue: valueObject.dataStringExtraValue
        };
        detailInfo.key = {
          dataStringValue: valueObject.dataStringValue,
          dataStringExtraValue: valueObject.dataStringExtraValue
        };
      }
    } else {
      detailInfo.value =
        valueObject.dataStringValue || valueObject.dataStringValues || '';
      detailInfo.key =
        valueObject.dataStringValue || valueObject.dataStringValues || '';
    }
  } else {
  }
};

/**
 * True for a field whose value is DERIVED and never stored — contact Age, for example, which the
 * server computes from Date of Birth on every read.
 *
 * These must not get a form control. prepareDetailDataRequest submits a value for every control it
 * finds, so a control here would post the computed value straight back to be saved, which is exactly
 * what a derived field must never do. There is nothing to type either — the value is read-only by
 * definition — so the editable form simply omits them; the read-only record view still shows them.
 */
export const isDerivedReadOnlyField = (detailInfo: DetailInfo): boolean =>
  detailInfo?.dataSourceType === InputDataSourceTypes.DynamicReadOnly;

export const generateFormGroupForInputContentGroup = (
  fb: FormBuilder,
  group: DetailGroupInfo
): FormGroup => {
  const formGroup = fb.group({});
  group.detailInfos.forEach((detailInfo) => {
    if (isDerivedReadOnlyField(detailInfo)) {
      return;
    }
    formGroup.addControl(
      detailInfo.evideInputContentGuid,
      generateFormGroupForDetailInfo(formGroup, detailInfo)
    );
  });
  return formGroup;
};

export const generateFormGroupForDetailInfo = (
  forGroup: FormGroup,
  detailInfo: DetailInfo
): FormGroup => {
  const validators = [];
  if (detailInfo.isMandatory) {
    validators.push(Validators.required);
    if (detailInfo.controllerType === InputControllerTypes.DropdownWithExtraText) {
      validators.push(optionWithCommentRequired);
    }
  }
  if (detailInfo.regEx) {
    validators.push(Validators.pattern(detailInfo.regEx));
  }

  const controllerType: InputControllerTypes = detailInfo.controllerType;

  // RepeatableTable: the control is a FormArray of row FormGroups (not a scalar FormControl).
  if (controllerType === InputControllerTypes.RepeatableTable) {
    forGroup.addControl(
      detailInfo.evideInputContentGuid,
      generateFormArrayForTableField(detailInfo)
    );
    return forGroup;
  }

  const value = valueToControllerType(controllerType, detailInfo.key);

  forGroup.addControl(
    detailInfo.evideInputContentGuid,
    new FormControl(value, { validators })
  );

  return forGroup;
};

export const applyVariantVisibilityToFormGroup = (
  rootFormGroup: FormGroup,
  detailGroupInfos: DetailGroupInfo[],
  selectedVariantGuid: string | null
): void => {
  if (!rootFormGroup || !detailGroupInfos) return;

  detailGroupInfos.forEach((group) => {
    const groupControl = rootFormGroup.controls[group.uuid];
    if (!(groupControl instanceof FormGroup)) return;

    group.detailInfos.forEach((detailInfo) => {
      const fieldControl = groupControl.controls[detailInfo.evideInputContentGuid];
      if (!fieldControl) return;

      const hiddenByVariant = detailInfo.computeHiddenByVariant(selectedVariantGuid);
      detailInfo.isHiddenByCurrentVariant = hiddenByVariant;

      if (hiddenByVariant) {
        // Strip validators so a previously-required field doesn't keep the form invalid
        // while invisible, then disable so its value is excluded from formGroup.value.
        fieldControl.clearValidators();
        if (fieldControl.enabled) {
          fieldControl.disable({ emitEvent: false });
        }
      } else {
        // Restore validators from the DetailInfo definition (same logic as
        // generateFormGroupForDetailInfo) and re-enable.
        const validators = [];
        if (detailInfo.isMandatory) {
          validators.push(Validators.required);
          if (detailInfo.controllerType === InputControllerTypes.DropdownWithExtraText) {
            validators.push(optionWithCommentRequired);
          }
        }
        if (detailInfo.regEx) validators.push(Validators.pattern(detailInfo.regEx));
        fieldControl.setValidators(validators);
        if (fieldControl.disabled) {
          fieldControl.enable({ emitEvent: false });
        }
      }
      fieldControl.updateValueAndValidity({ emitEvent: false });
    });
  });
};

// Referral workflow: per-action field visibility on top of variant visibility. Refer hides the
// mapped Start Date and requires the Referral Date; Accept requires the Start Date. Runs AFTER
// applyVariantVisibilityToFormGroup (which recomputes isHiddenByCurrentVariant and validators
// from the definition), so a variant switch cannot un-hide or un-require these fields. A
// hidden control also has its value cleared: the wizard posts getRawValue(), which includes
// disabled controls, and a stale start date must never be stamped on a referral.
// Therapy approval: read-only fields keep their value (posted via getRawValue) but can't be
// changed - the approver set them.
export const applyFieldModeToFormGroup = (
  rootFormGroup: FormGroup,
  detailGroupInfos: DetailGroupInfo[],
  hiddenFieldGuids: string[],
  requiredFieldGuids: string[],
  readOnlyFieldGuids: string[] = []
): void => {
  if (!rootFormGroup || !detailGroupInfos) return;
  const hidden = new Set((hiddenFieldGuids ?? []).filter(Boolean).map((g) => g.toLowerCase()));
  const required = new Set((requiredFieldGuids ?? []).filter(Boolean).map((g) => g.toLowerCase()));
  const readOnly = new Set((readOnlyFieldGuids ?? []).filter(Boolean).map((g) => g.toLowerCase()));
  if (!hidden.size && !required.size && !readOnly.size) return;

  detailGroupInfos.forEach((group) => {
    const groupControl = rootFormGroup.controls[group.uuid];
    if (!(groupControl instanceof FormGroup)) return;

    group.detailInfos.forEach((detailInfo) => {
      const guid = (detailInfo.evideInputContentGuid ?? '').toLowerCase();
      const fieldControl = groupControl.controls[detailInfo.evideInputContentGuid];
      if (!fieldControl) return;

      if (hidden.has(guid)) {
        detailInfo.isHiddenByCurrentVariant = true;
        fieldControl.clearValidators();
        fieldControl.setValue(null, { emitEvent: false });
        if (fieldControl.enabled) {
          fieldControl.disable({ emitEvent: false });
        }
        fieldControl.updateValueAndValidity({ emitEvent: false });
      } else if (readOnly.has(guid)) {
        fieldControl.clearValidators();
        if (fieldControl.enabled) {
          fieldControl.disable({ emitEvent: false });
        }
        fieldControl.updateValueAndValidity({ emitEvent: false });
      } else if (required.has(guid)) {
        fieldControl.addValidators(Validators.required);
        fieldControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  });
};

export const prepareDetailDataRequest = (
  formObj: any,
  detailInfoList: DetailInfo[]
): IDetailInfoDataHttp[] => {
  const detailInfoData: IDetailInfoDataHttp[] = [];

  Object.keys(formObj).forEach((key: string) => {
    if (!key.startsWith(DetailGroupInfo.KeyInitials)) {
      // Ignore items except question group like recaptcha.
      return;
    }
    const mappings = formObj[key];
    Object.keys(mappings).forEach((mappingGuid) => {
      let value = mappings[mappingGuid];

      const detailInfo: DetailInfo | undefined = detailInfoList.find(
        (item) => item.evideInputContentGuid === mappingGuid
      );

      if (detailInfo?.isHiddenByCurrentVariant) {
        return;
      }

      // RepeatableTable: the control value is an array of row objects (keyed by column guid).
      // Serialise each cell by its COLUMN's controller type into tableRows.
      if (detailInfo?.controllerType === InputControllerTypes.RepeatableTable) {
        const columns = parseRepeatableTableColumns(detailInfo);
        const fields = columns.reduce<DetailInfo[]>((acc, c) => acc.concat(c.fields || []), []);
        const rows: any[] = Array.isArray(value) ? value : [];
        const tableRows = rows.map((rowObj: any) =>
          fields.map((field) => ({
            evideInputContentGuid: field.evideInputContentGuid,
            ...inputControllerTypesToValue(field.controllerType, rowObj?.[field.evideInputContentGuid])
          } as IDetailInfoDataHttp))
        );
        detailInfoData.push({
          evideInputContentGuid: mappingGuid,
          tableRows
        } as IDetailInfoDataHttp);
        return;
      }

      const stringValues = inputControllerTypesToValue(
        detailInfo?.controllerType,
        value
      );

      const payload: IDetailInfoDataHttp = {
        evideInputContentGuid: mappingGuid,
        ...stringValues
      };

      detailInfoData.push(payload);
    });
  });
  return detailInfoData;
};

export const compareValidationResult = (
  detailInfoRequestData: IDetailInfoDataHttp[],
  detailInfoList: DetailInfo[]
) => {
  let errorMessage = '';
  let isValid = true;

  for (let index = 0; index < detailInfoRequestData.length; index++) {
    const detailInfoData = detailInfoRequestData[index];

    let detailInfo = detailInfoList.find(
      (x) => x.evideInputContentGuid == detailInfoData.evideInputContentGuid
    );
    if (!detailInfo || !detailInfo.validationInfos) continue;
    if (detailInfo.isHiddenByCurrentVariant) continue;
    for (let index = 0; index < detailInfo.validationInfos.length; index++) {
      const validationInfos = detailInfo.validationInfos[index];
      let targetInfo = detailInfoList.find(
        (x) =>
          x.evideInputContentGuid == validationInfos.targetEvideInputContentGuid
      );
      if (!targetInfo) continue;
      if (targetInfo.isHiddenByCurrentVariant) continue;
      if (detailInfo.dataType == targetInfo.dataType) {
        let targetValidationInfo = detailInfoRequestData.find(
          (x) =>
            x.evideInputContentGuid ==
            validationInfos.targetEvideInputContentGuid
        );
        if (!targetValidationInfo) continue;
        let operation = validationInfos.operation;
        var operationName = getOperationName(operation);
        if (
          detailInfo.dataType == DataTypes.Boolean ||
          (detailInfoData.dataStringValue &&
            targetValidationInfo.dataStringValue)
        ) {
          switch (detailInfo.dataType) {
            case DataTypes.String:
              {
                isValid = checkValidationOperation<string>(
                  detailInfoData.dataStringValue,
                  targetValidationInfo.dataStringValue,
                  operation
                );
                if (!isValid) {
                  errorMessage = `Validation failed: ${detailInfo.title} ${operationName} ${targetInfo.title}`;
                }
              }
              break;
            case DataTypes.Integer:
            case DataTypes.Double:
              {
                isValid = checkValidationOperation<Number>(
                  Number(detailInfoData.dataStringValue),
                  Number(targetValidationInfo.dataStringValue),
                  operation
                );
                if (!isValid) {
                  errorMessage = `Validation failed: ${detailInfo.title} ${operationName} ${targetInfo.title}`;
                }
              }
              break;
            case DataTypes.DateTime:
              {
                isValid = checkValidationOperation<Date>(
                  new Date(detailInfoData.dataStringValue),
                  new Date(targetValidationInfo.dataStringValue),
                  operation
                );
                if (!isValid) {
                  errorMessage = `Validation failed: ${detailInfo.title} ${operationName} ${targetInfo.title}`;
                }
              }
              break;
            case DataTypes.TimeSpan:
              {
                isValid = checkValidationOperation<string>(
                  detailInfoData.dataStringValue,
                  targetValidationInfo.dataStringValue,
                  operation
                );
                if (!isValid) {
                  errorMessage = `Validation failed: ${detailInfo.title} ${operationName} ${targetInfo.title}`;
                }
              }
              break;
            case DataTypes.Boolean:
              {
                isValid = checkValidationOperation<Boolean>(
                  Boolean(JSON.parse(detailInfoData.dataStringValue)),
                  Boolean(JSON.parse(targetValidationInfo.dataStringValue)),
                  operation
                );
                if (!isValid) {
                  errorMessage = `Validation failed: ${detailInfo.title} ${operationName} ${targetInfo.title}`;
                }
              }
              break;
            default:
              break;
          }
        }
      }
      if (!isValid) {
        break;
      }
    }
    if (!isValid) {
      break;
    }
  }
  return { isValid, errorMessage };
};

export const getOperationName = (operation) => {
  switch (operation) {
    case '>': {
      return 'should be greater than';
    }
    case '<': {
      return 'should be less than';
    }
    case '>=': {
      return 'should be greater than or equal to';
    }
    case '<=': {
      return 'should be less than or equal to';
    }
    case '!=': {
      return 'should not equal to';
    }
  }
};

export const checkValidationOperation = <T>(
  dataStringValue1: T,
  dataStringValue2: T,
  operation: string
) => {
  switch (operation) {
    case '>': {
      return dataStringValue1 > dataStringValue2;
    }
    case '<': {
      return dataStringValue1 < dataStringValue2;
    }
    case '>=': {
      return dataStringValue1 >= dataStringValue2;
    }
    case '<=': {
      return dataStringValue1 <= dataStringValue2;
    }
    case '!=': {
      return dataStringValue1 != dataStringValue2;
    }
    default:
      break;
  }
};

// Read-only output for a RAW saved cell (DetailData shape, not a form-control value): resolve a
// table cell's stored value to display text via the column's definition. Used by the read-only
// detail views (registration details etc.) to render saved RepeatableTable rows.
export const displayValueForRawCell = (
  field: DetailInfo,
  rowCells: IDetailValue[]
): string => {
  const cloned = field.clone();
  assignDetailInfoValue(cloned, rowCells || []);

  const raw = cloned.key;
  switch (field.controllerType) {
    case InputControllerTypes.DatePicker: {
      const m = moment(raw as string, ['DD/MM/YYYY HH:mm:ss', 'MM/DD/YYYY HH:mm:ss', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601], false);
      return raw && m.isValid() ? m.format('DD/MM/YYYY') : (raw ? String(raw) : '-');
    }
    case InputControllerTypes.DateTimePicker: {
      const m = moment(raw as string, ['YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY HH:mm:ss', moment.ISO_8601], false);
      return raw && m.isValid() ? m.format('DD/MM/YYYY HH:mm') : (raw ? String(raw) : '-');
    }
    case InputControllerTypes.TimePicker:
      return raw ? String(raw) : '-';
    case InputControllerTypes.Toggle:
      return raw === 'true' || raw === true as any ? 'Yes' : raw === 'false' || raw === false as any ? 'No' : '-';
    default: {
      const text = cloned.displayStringValue;
      return text && String(text).trim() ? String(text).trim() : '-';
    }
  }
};

// Read-only output: resolve a form-control VALUE for a field to display text (option labels,
// formatted dates, Yes/No toggles) instead of showing a disabled control. Used by the
// repeatable grid's view mode and the action plan footer's view mode.
export const displayValueForField = (field: DetailInfo, value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const optionLabel = (key: any): string => {
    const found =
      (field.inputContentDatas || []).find((o) => o.key == key) ??
      resolveBooleanSynonymOption(field.inputContentDatas || [], key);
    return found ? found.value : String(key);
  };
  const withExtra = (v: any): string => {
    if (v && typeof v === 'object') {
      if (!v.dataStringValue) {
        return '';
      }
      const label = optionLabel(v.dataStringValue);
      return v.dataStringExtraValue ? `${label} (${v.dataStringExtraValue})` : label;
    }
    return optionLabel(v);
  };

  switch (field.controllerType) {
    case InputControllerTypes.Dropdown:
      return optionLabel(value) || '-';
    case InputControllerTypes.DropdownMulti:
      return Array.isArray(value) && value.length ? value.map(optionLabel).join(', ') : '-';
    case InputControllerTypes.RadioButtons:
    case InputControllerTypes.DropdownWithExtraText:
      return withExtra(value) || '-';
    case InputControllerTypes.Checkbox:
    case InputControllerTypes.Checkboxes:
      return Array.isArray(value) && value.length
        ? value.map(withExtra).filter(Boolean).join(', ') || '-'
        : '-';
    case InputControllerTypes.DatePicker:
      return moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : '-';
    case InputControllerTypes.DateTimePicker:
      return moment(value).isValid() ? moment(value).format('DD/MM/YYYY HH:mm') : '-';
    case InputControllerTypes.TimePicker:
      return moment(value).isValid() ? moment(value).format('HH:mm') : '-';
    case InputControllerTypes.Toggle:
      return value === true || value === 'true' ? 'Yes' : 'No';
    default:
      return Array.isArray(value) ? value.join(', ') : String(value);
  }
};

export const flatternDetailInfoFromGroup = (
  detailGroupInfoList: DetailGroupInfo[]
): DetailInfo[] => {
  const detailInfo: DetailInfo[] = [];
  detailGroupInfoList.forEach((group) => {
    group.detailInfos.forEach((programContact) => {
      detailInfo.push(programContact);
    });
  });
  return detailInfo;
};

export const DaysOfWeek = [
  { index: 1, id: 'MON', name: 'Monday', selected: false },
  { index: 2, id: 'TUE', name: 'Tuesday', selected: false },
  { index: 3, id: 'WED', name: 'Wednesday', selected: false },
  { index: 4, id: 'THU', name: 'Thursday', selected: false },
  { index: 5, id: 'FRI', name: 'Friday', selected: false },
  { index: 6, id: 'SAT', name: 'Saturday', selected: false },
  { index: 0, id: 'SUN', name: 'Sunday', selected: false }
];

export const dayIndexToCron = (dayIndex: number): string => {
  const day = DaysOfWeek.find(({ index }) => index === dayIndex)?.id;
  let cron = '';
  if (day) {
    cron = `0 0 0 ? * ${day} *`;
  }
  return cron;
};

export const isAnyFieldLoading = (
  gridRowLists: AttendanceGridRow[]
): boolean => {
  return gridRowLists.some(({ attendanceContacts }) => {
    return attendanceContacts.some(({ detailInfo }) => {
      return !!detailInfo?.isFieldLoading;
    });
  });
};
